import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'ts-debounce';
import type { AutocompleteOption } from '../../../types';

interface UseAutocompleteDataArgs<TJson> {
    url: string;
    headers?: HeadersInit;
    mapOptions: (json: TJson) => AutocompleteOption[];
    minChars: number;
    onSelect: (opt: AutocompleteOption | null, raw?: string) => void;
    onFetchFinally?: () => void;
}

export function useAutocompleteData<TJson = unknown>({
    url,
    headers,
    mapOptions,
    minChars,
    onSelect,
    onFetchFinally,
}: UseAutocompleteDataArgs<TJson>) {
    const [text, setText] = useState('');
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [fetching, setFetching] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    const mapOptionsRef = useRef(mapOptions);
    const headersRef = useRef(headers);

    useEffect(() => {
        mapOptionsRef.current = mapOptions;
    }, [mapOptions]);

    useEffect(() => {
        headersRef.current = headers;
    }, [headers]);

    const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const fetchCountRef = useRef(0);

    const fetchSuggestions = (value: string) => {
        loaderTimeoutRef.current = setTimeout(() => setShowLoader(true), 500);
        const fetchNum = ++fetchCountRef.current;

        setShowDropdown(true);
        setFetching(true);

        fetch(`${url}${encodeURIComponent(value)}`, { headers: headersRef.current })
            .then(async (res) => {
                if (!res.ok) {
                    const body = await res.text().catch(() => '');
                    throw new Error(`[autocomplete] HTTP ${res.status} ${res.statusText} – ${body.slice(0, 120)}`);
                }

                const txt = await res.text();

                if (!txt) return [];

                try {
                    return JSON.parse(txt);
                } catch {
                    throw new Error('[autocomplete] Invalid JSON response');
                }
            })

            .then((json) => {
                if (fetchNum !== fetchCountRef.current) return;
                let mapped: AutocompleteOption[] = [];

                if (Array.isArray(json)) {
                    mapped = mapOptionsRef.current(json as TJson);
                }
                setOptions(mapped);
                setNoResults(!Array.isArray(json) || mapped.length === 0);
            })

            .catch((err) => {
                console.error('[autocomplete] fetchSuggestions error:', err);
                setOptions([]);
                setNoResults(true);
            })

            .finally(() => {
                if (loaderTimeoutRef.current) {
                    clearTimeout(loaderTimeoutRef.current);
                    loaderTimeoutRef.current = null;
                }
                setShowLoader(false);
                setFetching(false);
                onFetchFinally?.();
            });
    };

    const debouncedFetch = useMemo(() => debounce(fetchSuggestions, 200), [url]);

    const handleInputChange = (value: string) => {
        setText(value);

        if (value.length === 0) {
            setOptions([]);
            setNoResults(false);
            onSelect(null);
            setShowDropdown(false);

            return;
        }

        onSelect(null, value);

        if (value.length >= minChars) {
            setShowDropdown(true);
            debouncedFetch(value);
        } else {
            setOptions([]);
            setNoResults(false);
        }
    };

    const clearAll = () => {
        setText('');
        setOptions([]);
        setNoResults(false);
        onSelect(null);
    };

    useEffect(() => () => debouncedFetch.cancel?.(), [debouncedFetch]);

    return {
        text,
        setText,
        options,
        setOptions,
        fetching,
        noResults,
        showDropdown,
        setShowDropdown,
        showLoader,
        handleInputChange,
        clearAll,
        setNoResults,
    };
}
